import { LinkProps, useNavigate } from "@tanstack/react-router"
import { ChevronUp, LucideIcon } from "lucide-react"
import * as React from "react"
import { Button, ButtonLink } from "../atoms/Button"
import { Skeleton } from "../atoms/Skeleton"
import { cn } from "../utils"

function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b [&_tr]:hover:bg-background sticky top-0 z-10 h-12", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn("bg-muted/50 border-t font-medium [&>tr]:last:border-b-0", className)}
      {...props}
    />
  )
}

function TableRow({
  className,
  navigate,
  ...props
}: React.ComponentProps<"tr"> & { navigate?: LinkProps }) {
  const navigation = useNavigate()

  return (
    <tr
      data-slot="table-row"
      className={cn(
        "hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
        className,
      )}
      {...props}
      {...(navigate && {
        onClick: (ev) => {
          ev.stopPropagation()
          navigation(navigate)
        },
      })}
    />
  )
}

type SortableArrowProps = {
  active: boolean
  direction: "asc" | "desc"
  onClick: () => void
}

function SortableArrow({ direction, active }: SortableArrowProps) {
  return (
    <ChevronUp
      className={cn(
        "h-4 w-4 transition-transform duration-150 shrink-0",
        !active && "opacity-40",
        direction === "desc" ? "rotate-180" : "rotate-0",
      )}
    />
  )
}

function TableHead({
  className,
  colSpan = 1,
  align = "left",
  noPadding = false,
  sortable,
  ...props
}: React.ComponentProps<"th"> & {
  sortable?: { active: boolean; direction: "asc" | "desc"; onClick: () => void }
  noPadding?: boolean
}) {
  const Component = sortable?.onClick ? "button" : "span"

  return (
    <th
      data-slot="table-head"
      colSpan={colSpan}
      className={cn(
        "h-12 text-left align-middle font-semibold",
        noPadding && "px-0 py-0",
        className,
      )}
      {...props}
    >
      <div
        className={cn(
          "flex items-center px-4",
          align === "left" && "justify-start",
          align === "center" && "justify-center",
          align === "right" && "justify-end",
          noPadding && "px-0 py-0",
        )}
      >
        <Component
          onClick={sortable?.onClick}
          className={cn(
            "flex flex-row items-center gap-1 text-foreground whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
            sortable && "cursor-pointer",
          )}
        >
          {props.children}
          {sortable && <SortableArrow {...sortable} />}
        </Component>
      </div>
    </th>
  )
}

function TableCell({
  className,
  colSpan = 1,
  align = "left",
  noPadding = false,
  ...props
}: React.ComponentProps<"td"> & { noPadding?: boolean }) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        "h-12 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px] px-4 py-3",
        noPadding && "px-0 py-0",
        className,
      )}
      align={align}
      colSpan={colSpan}
      {...props}
    >
      {props.children}
    </td>
  )
}

function TableCaption({ className, ...props }: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  )
}

const SkeletonWidths = ["w-1/3", "w-1/2", "w-2/3", "w-3/4", "w-2/5"]

type TableSkeletonProps = {
  numRows?: number
  colSpans: number[]
}

const TableSkeleton = React.memo(
  ({ numRows = 5, colSpans }: TableSkeletonProps) => {
    return (
      <>
        {Array.from({ length: numRows }).map((_, i) => (
          <TableRow key={i}>
            {colSpans.map((colSpan, j) => (
              <TableCell key={j} colSpan={colSpan}>
                <Skeleton
                  className={`h-3 my-2.5 ${SkeletonWidths[Math.floor(Math.random() * 5)]}`}
                />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </>
    )
  },
  (prevProps, currentProps) => {
    return (
      prevProps.numRows === currentProps.numRows &&
      JSON.stringify(prevProps.colSpans) === JSON.stringify(currentProps.colSpans)
    )
  },
)
TableSkeleton.displayName = "TableSkeleton"

type TableEmptySlateProps = {
  colSpan: number
  icon?: LucideIcon
  title?: string
  message: string
  cta?: {
    label: string
    variant?: React.ComponentProps<typeof ButtonLink>["variant"]
  } & LinkProps
  className?: string
}

const TableEmptySlate = ({
  colSpan,
  title,
  message,
  cta,
  icon: Icon,
  className,
}: TableEmptySlateProps) => {
  return (
    <TableRow className={cn("bg-bg hover:bg-bg", className)}>
      <TableCell
        colSpan={colSpan}
        className="py-8 text-center justify-center items-center flex-col"
      >
        {Icon && (
          <div className="flex items-center justify-center pb-2">
            {<Icon className="w-12 h-12" />}
          </div>
        )}
        {title && <p className="font-semibold">{title}</p>}
        <p>{message}</p>
        {cta && cta.to && (
          <ButtonLink
            to={cta.to}
            variant={cta.variant ?? "outline"}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            search={(prev: any) => cta.to?.includes(location.pathname) && prev}
            className="mt-4"
          >
            {cta.label}
          </ButtonLink>
        )}
        {cta && cta.href && (
          <a {...cta} href={cta.href}>
            <Button variant={cta.variant ?? "outline"} className="mt-4">
              {cta.label}
            </Button>
          </a>
        )}
      </TableCell>
    </TableRow>
  )
}
TableEmptySlate.displayName = "TableEmptySlate"

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableEmptySlate,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
}
